/**
 * Tool Execution System
 * Handles different tool types: scoring_calculator, matrix, quiz, checklist, rag_only
 */

/**
 * Execute a tool based on its type
 * @param {Object} tool - Tool object with tool_type and tool_config
 * @param {Object} conversationData - Current conversation context
 * @param {Function} queryRAG - Function to query RAG for tool content
 * @returns {Object} Tool execution result
 */
export const executeTool = async (tool, conversationData, queryRAG) => {
  // Priority: If tool has compute function, use it (even if tool_type is set)
  // This allows tools like ROI Calculator to use custom logic
  if (tool.compute && typeof tool.compute === 'function') {
    const result = tool.compute(conversationData);
    // Ensure result has toolType for renderer
    return {
      ...result,
      toolType: result.toolType || 'computed'
    };
  }
  
  const toolType = tool.tool_type || 'rag_only';
  const toolConfig = tool.tool_config || {};

  switch (toolType) {
    case 'scoring_calculator':
      return executeScoringCalculator(tool, toolConfig, conversationData);
    
    case 'matrix':
      return executeMatrix(tool, toolConfig, conversationData);
    
    case 'quiz':
      return executeQuiz(tool, toolConfig, conversationData);
    
    case 'checklist':
      return executeChecklist(tool, toolConfig, conversationData);
    
    case 'computed':
      // Fall through to RAG if no compute function
    
    case 'rag_only':
    default:
      // Fallback: Query RAG for tool content
      return await executeRAGTool(tool, queryRAG);
  }
};

/**
 * Execute scoring calculator tool
 * Example: 3-Lever Execution Accelerator, Stress Load Index
 */
const executeScoringCalculator = (tool, config, conversationData) => {
  const fields = config.fields || [];
  const calculation = config.calculation || 'sum';
  const recommendations = config.recommendations || {};
  
  // Extract values from conversation or return empty for user input
  const fieldValues = {};
  const missingFields = [];
  
  fields.forEach(field => {
    const value = extractFieldValue(field.name, conversationData, field);
    if (value !== null && value !== undefined) {
      fieldValues[field.name] = value;
    } else {
      missingFields.push(field);
    }
  });
  
  // If all fields have values, calculate score
  let score = null;
  let recommendation = null;
  
  if (missingFields.length === 0 && fields.length > 0) {
    score = calculateScore(fieldValues, fields, calculation, config.weights);
    
    // Find matching recommendation
    recommendation = findRecommendation(score, recommendations);
  }
  
  return {
    toolType: 'scoring_calculator',
    fields: fields.map(f => ({
      ...f,
      value: fieldValues[f.name],
      label: f.label || f.name
    })),
    missingFields,
    score,
    recommendation,
    recommendations,
    actions: config.actions || [],
    followUp: config.followUp || (missingFields.length > 0 ? 
      `Please provide: ${missingFields.map(f => f.label || f.name).join(', ')}` : null)
  };
};

/**
 * Execute matrix tool (2x2, priority matrix, etc.)
 */
const executeMatrix = (tool, config, conversationData) => {
  const axes = config.axes || [];
  const quadrants = config.quadrants || {};
  
  if (axes.length < 2) {
    return {
      toolType: 'matrix',
      error: 'Matrix requires at least 2 axes',
      axes: [],
      quadrants: {}
    };
  }
  
  // Extract axis values
  const axisValues = {};
  const missingAxes = [];
  
  axes.forEach(axis => {
    const value = extractFieldValue(axis.name, conversationData, axis);
    if (value !== null && value !== undefined) {
      axisValues[axis.name] = value;
    } else {
      missingAxes.push(axis);
    }
  });
  
  // Determine quadrant if both axes have values
  let currentQuadrant = null;
  if (missingAxes.length === 0 && axes.length >= 2) {
    const xValue = axisValues[axes[0].name];
    const yValue = axisValues[axes[1].name];
    const xMidpoint = (axes[0].min + axes[0].max) / 2;
    const yMidpoint = (axes[1].min + axes[1].max) / 2;
    
    // Determine quadrant (assuming high/low split)
    const quadrantKey = 
      (xValue >= xMidpoint ? 'high' : 'low') + '_' +
      (yValue >= yMidpoint ? 'high' : 'low');
    
    currentQuadrant = quadrants[quadrantKey] || quadrants['default'];
  }
  
  return {
    toolType: 'matrix',
    axes: axes.map(a => ({
      ...a,
      value: axisValues[a.name],
      label: a.label || a.name
    })),
    missingAxes,
    currentQuadrant,
    quadrants,
    followUp: missingAxes.length > 0 ?
      `Please provide: ${missingAxes.map(a => a.label || a.name).join(' and ')}` : null
  };
};

/**
 * Execute quiz tool
 */
const executeQuiz = (tool, config, conversationData) => {
  const questions = config.questions || [];
  const scoring = config.scoring || {};
  
  // For now, return quiz structure - interactive component will handle answers
  return {
    toolType: 'quiz',
    questions,
    scoring,
    actions: config.actions || []
  };
};

/**
 * Execute checklist tool
 */
const executeChecklist = (tool, config, conversationData) => {
  const items = config.items || [];
  
  return {
    toolType: 'checklist',
    items,
    actions: config.actions || []
  };
};

/**
 * Execute RAG-only tool (fallback)
 */
const executeRAGTool = async (tool, queryRAG) => {
  if (!queryRAG) {
    return {
      toolType: 'rag_only',
      content: tool.description || 'This tool will provide guidance based on your uploaded framework.',
      actions: ['Ask questions about this framework', 'Use this framework in your conversation']
    };
  }
  
  // Query RAG for tool content
  try {
    const ragContent = await queryRAG(tool.name || tool.id, tool.sourceDoc || tool.doc_id);
    return {
      toolType: 'rag_only',
      content: ragContent,
      actions: ['Ask questions about this framework', 'Use this framework in your conversation']
    };
  } catch (error) {
    console.error('[Tool Executor] RAG query failed:', error);
    return {
      toolType: 'rag_only',
      content: tool.description || 'This tool will provide guidance based on your uploaded framework.',
      actions: ['Ask questions about this framework', 'Use this framework in your conversation']
    };
  }
};

/**
 * Extract field value from conversation data
 */
const extractFieldValue = (fieldName, conversationData, fieldConfig) => {
  // Try direct match
  if (conversationData[fieldName]) {
    return conversationData[fieldName];
  }
  
  // Try variations
  const variations = [
    fieldName.replace(/_/g, ' '),
    fieldName.replace(/_/g, '-'),
    fieldConfig.label?.toLowerCase().replace(/\s+/g, '_')
  ];
  
  for (const variation of variations) {
    if (conversationData[variation]) {
      return conversationData[variation];
    }
  }
  
  return null;
};

/**
 * Calculate score based on field values
 */
const calculateScore = (fieldValues, fields, calculation, weights) => {
  if (calculation === 'sum') {
    return Object.values(fieldValues).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  }
  
  if (calculation === 'average') {
    const values = Object.values(fieldValues).map(v => parseFloat(v) || 0);
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  }
  
  if (calculation === 'weighted_sum' && weights) {
    let total = 0;
    let totalWeight = 0;
    fields.forEach(field => {
      const value = parseFloat(fieldValues[field.name]) || 0;
      const weight = weights[field.name] || 1;
      total += value * weight;
      totalWeight += weight;
    });
    return totalWeight > 0 ? total / totalWeight : 0;
  }
  
  // Default to sum
  return Object.values(fieldValues).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
};

/**
 * Find recommendation based on score
 */
const findRecommendation = (score, recommendations) => {
  if (!recommendations || typeof score !== 'number') {
    return null;
  }
  
  // Sort recommendation keys by score range
  const sortedRanges = Object.keys(recommendations).sort((a, b) => {
    const aMatch = a.match(/(\d+)-(\d+)|<(\d+)|>(\d+)/);
    const bMatch = b.match(/(\d+)-(\d+)|<(\d+)|>(\d+)/);
    
    if (!aMatch || !bMatch) return 0;
    
    const aMin = parseInt(aMatch[1] || aMatch[3] || aMatch[4] || '0');
    const bMin = parseInt(bMatch[1] || bMatch[3] || bMatch[4] || '0');
    
    return bMin - aMin; // Sort descending
  });
  
  // Find matching range
  for (const range of sortedRanges) {
    if (matchesRange(score, range)) {
      return {
        range,
        message: recommendations[range]
      };
    }
  }
  
  return null;
};

/**
 * Check if score matches a range string
 * Examples: "12-15", "<5", ">10"
 */
const matchesRange = (score, range) => {
  const rangeStr = range.trim();
  
  // Range: "12-15"
  const rangeMatch = rangeStr.match(/(\d+)-(\d+)/);
  if (rangeMatch) {
    const min = parseFloat(rangeMatch[1]);
    const max = parseFloat(rangeMatch[2]);
    return score >= min && score <= max;
  }
  
  // Less than: "<5"
  const lessMatch = rangeStr.match(/<(\d+)/);
  if (lessMatch) {
    return score < parseFloat(lessMatch[1]);
  }
  
  // Greater than: ">10"
  const greaterMatch = rangeStr.match(/>(\d+)/);
  if (greaterMatch) {
    return score > parseFloat(greaterMatch[1]);
  }
  
  return false;
};

